<script lang="ts">
  /** Comment nommer un genre de machine, le nôtre compris. */
  function genre(id: string): string {
    if (id === 'meka') return 'MEKA '
    if (id === 'drone') return 'Drone '
    return 'Essaim '
  }

  /**
   * Le garage : les Drones et MEKA que le personnage pilote.
   *
   * Les Points de vie d'une machine viennent du bestiaire — c'est le même Drone
   * de surveillance que celui qu'on peut affronter dans le tiroir de jet, et il
   * n'y a aucune raison d'en tenir deux comptes.
   */
  import { machines as lireLesMachines, type PersonnageEnregistre } from '../../../rules'

  let {
    personnage,
    garage,
    baptiser,
    abimer,
    boulonner,
  }: {
    personnage: PersonnageEnregistre
    /** Met une machine au garage, ou l'en retire. */
    garage: (modele: string | null, index: number | null) => void
    /** Baptise une machine. */
    baptiser: (index: number, nom: string) => void
    /** Encaisse des Dégâts sur une machine, ou en répare. */
    abimer: (index: number, subis: number) => void
    /** Boulonne un équipement sur une machine, ou le démonte. */
    boulonner: (index: number, equipement: string) => void
  } = $props()

  const parc = $derived(lireLesMachines(personnage))
  let atelier = $state(false)
  /** Celle dont on ouvre la caisse à outils. */
  let ouverte = $state<number | null>(null)
</script>

<div class="cartouche">
  <div class="bandeau">
    Machines
    {#if atelier}
      <button type="button" class="refermer" onclick={() => (atelier = false)}>Refermer</button>
    {/if}
  </div>
  <div class="corps">
    {#each parc.pilotees as m (m.index)}
      <section class="machine">
        <h3>
          <input
            class="saisie nom-machine"
            type="text"
            value={m.nom}
            aria-label="Nom de la machine"
            onchange={(e) => baptiser(m.index, e.currentTarget.value)}
          />
          <span class="voie"
            >{genre(m.genre)}{#if m.page}· p. {m.page}{:else}· rattaché à la table{/if}</span
          >
          <button type="button" class="lien" onclick={() => garage(null, m.index)}>Retirer</button>
        </h3>

        <div class="etat">
          <span class="chiffre" title="Points de vie">
            <span class="etiquette">PV</span>
            <span class="valeur data" class:trop={m.pv === 0}>{m.pv}/{m.pv_max}</span>
          </span>
          {#if m.vitesse !== null}
            <span class="chiffre" title="Vitesse">
              <span class="etiquette">VIT</span>
              <span class="valeur data">{m.vitesse}</span>
            </span>
          {/if}
          {#if m.protection > 0}
            <span class="chiffre" title="Points de protection">
              <span class="etiquette">PP</span>
              <span class="valeur data">{m.protection}</span>
            </span>
          {/if}
          {#if m.degats !== null || m.degats_montes !== null}
            <span class="chiffre" title="Dégâts">
              <span class="etiquette">DÉG</span>
              <span class="valeur data">
                {Math.max(m.degats ?? 0, m.degats_montes ?? 0)}
              </span>
            </span>
          {/if}
          <button type="button" class="pilule" onclick={() => abimer(m.index, 1)}>−1 PV</button>
          <button type="button" class="pilule" onclick={() => abimer(m.index, -1)}>+1 PV</button>
          <button
            type="button"
            class="pilule"
            aria-pressed={ouverte === m.index}
            onclick={() => (ouverte = ouverte === m.index ? null : m.index)}
          >
            Équiper
          </button>
        </div>

        <!-- Ce que son profil lui donne. Le bestiaire nomme ses Capacités sans
             les décrire ; les résoudre ici évite d'ouvrir le livre pour savoir
             ce que « Harcèlement » fait à qui s'approche. -->
        {#if m.capacites.length > 0}
          <ul class="plates capacites">
            {#each m.capacites as c (c.id)}
              <li>
                <span class="quoi">
                  <span class="nom">
                    {c.nom}
                    <span class="voie">{c.type_capacite === 'active' ? 'Active' : 'Passive'}</span>
                  </span>
                  <span class="detail">{c.resume}</span>
                </span>
              </li>
            {/each}
          </ul>
        {/if}

        <ul class="plates">
          {#each m.equipements as e (e.id)}
            <li>
              <span class="quoi">
                <span class="nom">{e.nom} <span class="voie">{e.genre}</span></span>
                <span class="detail">{e.effet}</span>
              </span>
              <button
                type="button"
                class="pilule discret"
                onclick={() => boulonner(m.index, e.id)}
              >
                Démonter
              </button>
            </li>
          {/each}
        </ul>

        {#if ouverte === m.index}
          <ul class="plates outils">
            {#each m.montables as e (e.id)}
              <li>
                <span class="quoi">
                  <span class="nom">{e.nom} <span class="voie">{e.genre}</span></span>
                  <span class="detail">{e.effet}</span>
                </span>
                <span class="gestes">
                  {#if e.prix_pi !== null}<span class="data">{e.prix_pi} PI</span>{/if}
                  <button type="button" class="pilule" onclick={() => boulonner(m.index, e.id)}>
                    Monter
                  </button>
                </span>
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    {/each}

    {#if !atelier}
      {#if parc.pilotees.length === 0}
        <p class="chapo">
          Rien au garage. Un Drone se pilote à distance, un MEKA se conduit — et
          les deux se boulonnent.
        </p>
      {/if}
      <button type="button" class="lien" onclick={() => (atelier = true)}>
        Ajouter une machine
      </button>
    {:else}
      <p class="regle">
        Le prix ne se décompte pas : le livre demande un plan, du travail et des
        Points d'Influence, ce qui se tranche à la table.
      </p>
      <ul class="achats larges">
        {#each parc.modeles as m (m.id)}
          <li>
            <span class="nom">
              {m.nom}
              <span class="exigence">
                {genre(m.genre)}· {m.pv} PV{#if m.page}· p. {m.page}{/if}
              </span>
            </span>
            <span class="chiffres data">
              {#if m.prix_pi !== null}{m.prix_pi} PI{/if}
              {#if m.prix_plan_livres !== null}plan {m.prix_plan_livres} £{/if}
            </span>
            <button type="button" class="pilule" onclick={() => garage(m.id, null)}>
              Au garage
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</div>

<style>
  /* Une machine : son nom se saisit, son état se lit d'un coup d'œil. */
  .machine {
    display: grid;
    gap: calc(var(--pas) / 2);
    padding-bottom: var(--pas);
    margin-bottom: var(--pas);
    border-bottom: 1px solid var(--trait);
  }
  .machine h3 {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .nom-machine {
    font-family: var(--titre);
    font-size: 0.95rem;
    max-width: 24ch;
  }
  .etat {
    display: flex;
    align-items: center;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .etat .chiffre {
    display: grid;
    justify-items: center;
    gap: calc(var(--pas) * 0.5);
    background: var(--surface-2);
    border: 1px solid var(--line);
    border-radius: var(--r);
    padding: calc(var(--pas) * 1.5);
  }
  .etiquette {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--ink-soft);
  }
  .etat .valeur.trop {
    color: var(--ko);
  }
  /* Ce qu'on peut encore boulonner : en retrait, comme un tiroir ouvert. */
  .outils {
    border-left: 2px solid var(--trait);
    padding-left: var(--pas);
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
</style>
